import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import AddCenterModal from './components/AddCenterModal';

const Settings = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showAddCenterModal, setShowAddCenterModal] = useState(false);

  // User Preferences State
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Notification Preferences State
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNotifications: true,
    smsNotifications: false,
    leadUpdates: true,
    inventoryAlerts: true,
    tenantReminders: true,
    systemUpdates: false,
    weeklyReports: true,
    monthlyReports: false
  });

  // Center Configuration State (Admin only)
  const [centerConfig, setCenterConfig] = useState({
    defaultCenter: '',
    autoAssignLeads: true,
    requireApproval: false,
    maxInventoryPerManager: '50'
  });

  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    theme: 'light',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeZone: 'America/New_York',
    itemsPerPage: '25',
    defaultView: 'grid'
  });

  useEffect(() => {
    if (userProfile) {
      setProfileData({
        fullName: userProfile?.full_name || '',
        email: user?.email || '',
        phone: userProfile?.phone || '',
        role: userProfile?.role || ''
      });
    }
  }, [userProfile, user]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Simulate save operation
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Password change error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCenterConfig = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSystemSettings = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        await signOut();
        navigate('/login');
      } catch (error) {
        console.error('Sign out error:', error);
      }
    }
  };

  const tabs = [
    { id: 'profile', label: 'User Profile', icon: 'User', roles: ['super_admin', 'admin', 'center_manager', 'sales'] },
    { id: 'notifications', label: 'Notifications', icon: 'Bell', roles: ['super_admin', 'admin', 'center_manager', 'sales'] },
    { id: 'centers', label: 'Center Config', icon: 'Building2', roles: ['super_admin', 'admin'] },
    { id: 'system', label: 'System Settings', icon: 'Settings', roles: ['super_admin', 'admin', 'center_manager', 'sales'] }
  ];

  const filteredTabs = tabs?.filter(tab => tab?.roles?.includes(userProfile?.role));

  const themeOptions = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'auto', label: 'Auto (System)' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
  ];

  const timeZoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }
  ];

  const itemsPerPageOptions = [
    { value: '10', label: '10 items' },
    { value: '25', label: '25 items' },
    { value: '50', label: '50 items' },
    { value: '100', label: '100 items' }
  ];

  const defaultViewOptions = [
    { value: 'grid', label: 'Grid View' },
    { value: 'list', label: 'List View' },
    { value: 'table', label: 'Table View' }
  ];

  const centerOptions = [
    { value: 'center-1', label: 'Downtown Hub' },
    { value: 'center-2', label: 'Tech Park' },
    { value: 'center-3', label: 'Business District' }
  ];

  const maxInventoryOptions = [
    { value: '25', label: '25 units' },
    { value: '50', label: '50 units' },
    { value: '100', label: '100 units' },
    { value: 'unlimited', label: 'Unlimited' }
  ];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
              aria-label="Go back"
            >
              <Icon name="ArrowLeft" size={24} className="text-foreground" />
            </button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <Icon name="Settings" size={32} />
                Settings
              </h1>
              <p className="text-muted-foreground mt-2">
                Manage your account preferences, notifications, and system settings
              </p>
            </div>
          </div>
        </div>

        {saveSuccess && (
          <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success-600" />
            <span className="text-success-900 font-medium">Settings saved successfully!</span>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="mb-6 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {filteredTabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  px-4 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors
                  ${activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                `}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* User Profile Tab */}
          {activeTab === 'profile' && (
            <>
              <Card>
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <Icon name="User" size={24} />
                      Profile Information
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Update your personal information and contact details
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      value={profileData?.fullName}
                      onChange={(e) => setProfileData({ ...profileData, fullName: e?.target?.value })}
                      placeholder="Enter your full name"
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      value={profileData?.email}
                      disabled
                      description="Email cannot be changed"
                    />
                    <Input
                      label="Phone Number"
                      type="tel"
                      value={profileData?.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e?.target?.value })}
                      placeholder="+1 (555) 000-0000"
                    />
                    <Input
                      label="Role"
                      value={profileData?.role}
                      disabled
                      description="Role is assigned by admin"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setProfileData({
                      fullName: userProfile?.full_name || '',
                      email: user?.email || '',
                      phone: userProfile?.phone || '',
                      role: userProfile?.role || ''
                    })}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveProfile} loading={loading}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <Icon name="Lock" size={24} />
                      Change Password
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Update your password to keep your account secure
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 max-w-md">
                    <Input
                      label="Current Password"
                      type="password"
                      value={passwordData?.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e?.target?.value })}
                      placeholder="Enter current password"
                    />
                    <Input
                      label="New Password"
                      type="password"
                      value={passwordData?.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e?.target?.value })}
                      placeholder="Enter new password"
                    />
                    <Input
                      label="Confirm New Password"
                      type="password"
                      value={passwordData?.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e?.target?.value })}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })}>
                      Cancel
                    </Button>
                    <Button onClick={handleChangePassword} loading={loading}>
                      Update Password
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Bell" size={24} />
                    Notification Preferences
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose how you want to receive notifications and updates
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Communication Channels */}
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Communication Channels</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="Email Notifications"
                        description="Receive notifications via email"
                        checked={notificationPrefs?.emailNotifications}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, emailNotifications: e?.target?.checked })}
                      />
                      <Checkbox
                        label="SMS Notifications"
                        description="Receive notifications via text message"
                        checked={notificationPrefs?.smsNotifications}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, smsNotifications: e?.target?.checked })}
                      />
                    </div>
                  </div>

                  {/* Activity Notifications */}
                  <div className="border-t border-border pt-6">
                    <h3 className="text-lg font-medium text-foreground mb-4">Activity Notifications</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="Lead Updates"
                        description="Get notified when leads are assigned or updated"
                        checked={notificationPrefs?.leadUpdates}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, leadUpdates: e?.target?.checked })}
                      />
                      <Checkbox
                        label="Inventory Alerts"
                        description="Receive alerts for inventory changes and availability"
                        checked={notificationPrefs?.inventoryAlerts}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, inventoryAlerts: e?.target?.checked })}
                      />
                      <Checkbox
                        label="Tenant Reminders"
                        description="Get reminders for lease expirations and tenant activities"
                        checked={notificationPrefs?.tenantReminders}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, tenantReminders: e?.target?.checked })}
                      />
                      {/* System Updates - admin and super_admin only */}
                      {['super_admin', 'admin']?.includes(userProfile?.role) && (
                        <Checkbox
                          label="System Updates"
                          description="Receive notifications about system maintenance and updates"
                          checked={notificationPrefs?.systemUpdates}
                          onChange={(e) => setNotificationPrefs({ ...notificationPrefs, systemUpdates: e?.target?.checked })}
                        />
                      )}
                    </div>
                  </div>

                  {/* Report Preferences - admin and super_admin only */}
                  {['super_admin', 'admin']?.includes(userProfile?.role) && (
                    <div className="border-t border-border pt-6">
                      <h3 className="text-lg font-medium text-foreground mb-4">Report Preferences</h3>
                      <div className="space-y-3">
                        <Checkbox
                          label="Weekly Reports"
                          description="Receive weekly summary reports via email"
                          checked={notificationPrefs?.weeklyReports}
                          onChange={(e) => setNotificationPrefs({ ...notificationPrefs, weeklyReports: e?.target?.checked })}
                        />
                        <Checkbox
                          label="Monthly Reports"
                          description="Receive comprehensive monthly reports"
                          checked={notificationPrefs?.monthlyReports}
                          onChange={(e) => setNotificationPrefs({ ...notificationPrefs, monthlyReports: e?.target?.checked })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button variant="outline">
                    Reset to Default
                  </Button>
                  <Button onClick={handleSaveNotifications} loading={loading}>
                    Save Preferences
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Center Configuration Tab (Admin & Super Admin) */}
          {activeTab === 'centers' && ['super_admin', 'admin']?.includes(userProfile?.role) && (
            <Card>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <Icon name="Building2" size={24} />
                      Center Configuration
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Manage center settings and add new centers
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowAddCenterModal(true)}
                    className="flex items-center gap-2"
                  >
                    <Icon name="Plus" size={18} />
                    Add Center
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Default Center */}
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Default Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Default Center"
                        description="Set the default center for new users"
                        options={centerOptions}
                        value={centerConfig?.defaultCenter}
                        onChange={(value) => setCenterConfig({ ...centerConfig, defaultCenter: value })}
                        placeholder="Select default center"
                      />
                      <Select
                        label="Max Inventory per Manager"
                        description="Maximum units a center manager can handle"
                        options={maxInventoryOptions}
                        value={centerConfig?.maxInventoryPerManager}
                        onChange={(value) => setCenterConfig({ ...centerConfig, maxInventoryPerManager: value })}
                      />
                    </div>
                  </div>

                  {/* Access Control */}
                  <div className="border-t border-border pt-6">
                    <h3 className="text-lg font-medium text-foreground mb-4">Access Control</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="Auto-assign Leads"
                        description="Automatically assign new leads to available sales representatives"
                        checked={centerConfig?.autoAssignLeads}
                        onChange={(e) => setCenterConfig({ ...centerConfig, autoAssignLeads: e?.target?.checked })}
                      />
                      <Checkbox
                        label="Require Admin Approval"
                        description="Require admin approval for center manager actions"
                        checked={centerConfig?.requireApproval}
                        onChange={(e) => setCenterConfig({ ...centerConfig, requireApproval: e?.target?.checked })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button variant="outline">
                    Reset to Default
                  </Button>
                  <Button onClick={handleSaveCenterConfig} loading={loading}>
                    Save Configuration
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* System Settings Tab */}
          {activeTab === 'system' && (
            <Card>
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Monitor" size={24} />
                    System Settings
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Customize your application preferences and display options
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Appearance */}
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Appearance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Theme"
                        description="Choose your preferred color theme"
                        options={themeOptions}
                        value={systemSettings?.theme}
                        onChange={(value) => setSystemSettings({ ...systemSettings, theme: value })}
                      />
                      <Select
                        label="Default View"
                        description="Set default view for list pages"
                        options={defaultViewOptions}
                        value={systemSettings?.defaultView}
                        onChange={(value) => setSystemSettings({ ...systemSettings, defaultView: value })}
                      />
                    </div>
                  </div>

                  {/* Localization */}
                  <div className="border-t border-border pt-6">
                    <h3 className="text-lg font-medium text-foreground mb-4">Localization</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Language"
                        description="Select your preferred language"
                        options={languageOptions}
                        value={systemSettings?.language}
                        onChange={(value) => setSystemSettings({ ...systemSettings, language: value })}
                      />
                      <Select
                        label="Date Format"
                        description="Choose how dates are displayed"
                        options={dateFormatOptions}
                        value={systemSettings?.dateFormat}
                        onChange={(value) => setSystemSettings({ ...systemSettings, dateFormat: value })}
                      />
                      <Select
                        label="Time Zone"
                        description="Set your local time zone"
                        options={timeZoneOptions}
                        value={systemSettings?.timeZone}
                        onChange={(value) => setSystemSettings({ ...systemSettings, timeZone: value })}
                      />
                    </div>
                  </div>

                  {/* Display Preferences */}
                  <div className="border-t border-border pt-6">
                    <h3 className="text-lg font-medium text-foreground mb-4">Display Preferences</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Items per Page"
                        description="Number of items to display per page"
                        options={itemsPerPageOptions}
                        value={systemSettings?.itemsPerPage}
                        onChange={(value) => setSystemSettings({ ...systemSettings, itemsPerPage: value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button variant="outline">
                    Reset to Default
                  </Button>
                  <Button onClick={handleSaveSystemSettings} loading={loading}>
                    Save Settings
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Danger Zone - Sign Out */}
        <Card className="border-destructive">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-destructive flex items-center gap-2">
                <Icon name="AlertTriangle" size={24} />
                Danger Zone
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Sign out of your account or perform other critical actions
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-foreground">Sign Out</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sign out from your account on this device
                  </p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={handleSignOut}
                  className="flex items-center gap-2"
                >
                  <Icon name="LogOut" size={18} />
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {showAddCenterModal && (
        <AddCenterModal
          onClose={() => setShowAddCenterModal(false)}
          onSuccess={() => {
            setShowAddCenterModal(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
          }}
        />
      )}
    </div>
  );
};

export default Settings;